import { useMemo } from "react";
import { toast } from "sonner";
import {
  ArrowDown, ArrowUp, Camera, Eye, EyeOff, Image as ImageIcon, Lock,
  Star, StarOff, Trash2, Upload,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/status";
import { MAX_PHOTO_BYTES, MAX_PHOTOS_PER_VEHICLE } from "./vehicleUtils";

export function VehiclePhotosTab({ vehicleId, images, editAllowed, uploading, setUploading, onChange }) {
  const sortedImages = useMemo(() => {
    return [...images].sort((a, b) => {
      // Cover first, then by order, then by created_at
      if (a.is_cover && !b.is_cover) return -1;
      if (!a.is_cover && b.is_cover) return 1;
      const ao = a.order ?? 9999;
      const bo = b.order ?? 9999;
      if (ao !== bo) return ao - bo;
      return (a.created_at || "").localeCompare(b.created_at || "");
    });
  }, [images]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error(`Photo too large (${(file.size / (1024 * 1024)).toFixed(1)}MB): max 10MB.`);
      return;
    }
    if (images.length >= MAX_PHOTOS_PER_VEHICLE) {
      toast.error(`Photo limit reached (max ${MAX_PHOTOS_PER_VEHICLE} per vehicle). Delete old photos first.`);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("is_public", "true");
      formData.append("is_cover", String(images.length === 0)); // first photo auto-cover
      await api.post(`/vehicles/${vehicleId}/photos`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
      await onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (imgId) => {
    if (!window.confirm("Remove this photo? This cannot be undone.")) return;
    try {
      await api.delete(`/vehicles/${vehicleId}/photos/${imgId}`);
      toast.success("Photo removed");
      await onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setCover = async (imgId) => {
    try {
      await api.put(`/vehicles/${vehicleId}/photos/${imgId}`, null, {
        params: { is_cover: true },
      });
      toast.success("Cover photo updated");
      await onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const togglePublic = async (img) => {
    try {
      await api.put(`/vehicles/${vehicleId}/photos/${img.id}`, null, {
        params: { is_public: !img.is_public },
      });
      toast.success(img.is_public ? "Photo hidden from public" : "Photo now visible publicly");
      await onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const move = async (imgId, direction) => {
    const idx = sortedImages.findIndex((p) => p.id === imgId);
    if (idx < 0) return;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= sortedImages.length) return;
    const reordered = [...sortedImages];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    try {
      await api.post(`/vehicles/${vehicleId}/photos/reorder`, {
        photo_ids: reordered.map((p) => p.id),
      });
      await onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const publicCount = images.filter((i) => i.is_public !== false).length;
  const remaining = MAX_PHOTOS_PER_VEHICLE - images.length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4" data-testid="vd-photos">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-lg font-semibold">Fleet photo gallery</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            JPG / PNG / WEBP / HEIC. Max 10MB per photo, up to {MAX_PHOTOS_PER_VEHICLE} per vehicle.
            <span className="ml-2 font-medium text-slate-700">
              {images.length}/{MAX_PHOTOS_PER_VEHICLE} used · {publicCount} public
            </span>
          </p>
        </div>
        {editAllowed && (
          <label
            className={`px-3 py-2 rounded-lg btn-brand text-sm inline-flex items-center gap-2 cursor-pointer ${
              (uploading || remaining <= 0) ? "opacity-60 pointer-events-none" : ""
            }`}
            data-testid="vd-photo-upload-label"
          >
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload photo"}
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              data-testid="vd-photo-upload-input"
              disabled={uploading || remaining <= 0}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleFile(f); }}
            />
          </label>
        )}
      </div>

      {sortedImages.length === 0 ? (
        <div className="text-sm text-slate-500 py-10 text-center border border-dashed border-slate-200 rounded-lg" data-testid="vd-photo-empty">
          <Camera className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          No photos yet. {editAllowed ? "Upload one to get started: first photo auto-becomes the cover." : "Ask a teammate with vehicle permission to add some."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vd-photo-grid">
          {sortedImages.map((img, idx) => (
            <PhotoCard
              key={img.id}
              img={img}
              editAllowed={editAllowed}
              isFirst={idx === 0}
              isLast={idx === sortedImages.length - 1}
              onRemove={() => removeImage(img.id)}
              onSetCover={() => setCover(img.id)}
              onTogglePublic={() => togglePublic(img)}
              onMoveUp={() => move(img.id, "up")}
              onMoveDown={() => move(img.id, "down")}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoCard({ img, editAllowed, isFirst, isLast, onRemove, onSetCover, onTogglePublic, onMoveUp, onMoveDown }) {
  const src = img.image_data ? `data:${img.content_type || "image/jpeg"};base64,${img.image_data}` : null;
  const isPublic = img.is_public !== false;
  return (
    <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50 group" data-testid={`vd-photo-${img.id}`}>
      <div className="aspect-[4/3] w-full bg-slate-100 grid place-items-center">
        {src ? <img src={src} alt={img.filename || img.caption || "Photo"} className="w-full h-full object-cover" /> : <ImageIcon className="w-8 h-8 text-slate-300" />}
      </div>

      {/* Top-left badges */}
      <div className="absolute top-2 left-2 flex flex-col gap-1">
        {img.is_cover && (
          <span className="px-2 py-0.5 bg-amber-500 text-white text-[10px] font-semibold rounded inline-flex items-center gap-1" data-testid={`vd-photo-cover-badge-${img.id}`}>
            <Star className="w-3 h-3" /> Cover
          </span>
        )}
        {!isPublic && (
          <span className="px-2 py-0.5 bg-slate-700 text-white text-[10px] font-semibold rounded inline-flex items-center gap-1" data-testid={`vd-photo-private-badge-${img.id}`}>
            <Lock className="w-3 h-3" /> Private
          </span>
        )}
      </div>

      <div className="p-2.5 space-y-1">
        <div className="text-xs font-medium truncate" title={img.filename}>{img.filename || img.caption || "Photo"}</div>
        <div className="text-[10px] text-slate-500">{fmtDate(img.created_at)}</div>
      </div>

      {editAllowed && (
        <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition" data-testid={`vd-photo-actions-${img.id}`}>
          {!img.is_cover && (
            <button
              onClick={onSetCover}
              data-testid={`vd-photo-set-cover-${img.id}`}
              title="Set as cover"
              className="p-1.5 rounded-md bg-white/95 hover:bg-amber-50 text-amber-700 shadow-sm"
            >
              <StarOff className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={onTogglePublic}
            data-testid={`vd-photo-toggle-public-${img.id}`}
            title={isPublic ? "Hide from public profile" : "Show on public profile"}
            className="p-1.5 rounded-md bg-white/95 hover:bg-slate-100 text-slate-700 shadow-sm"
          >
            {isPublic ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          </button>
          {!isFirst && (
            <button
              onClick={onMoveUp}
              data-testid={`vd-photo-move-up-${img.id}`}
              title="Move earlier"
              className="p-1.5 rounded-md bg-white/95 hover:bg-slate-100 text-slate-700 shadow-sm"
            >
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
          )}
          {!isLast && (
            <button
              onClick={onMoveDown}
              data-testid={`vd-photo-move-down-${img.id}`}
              title="Move later"
              className="p-1.5 rounded-md bg-white/95 hover:bg-slate-100 text-slate-700 shadow-sm"
            >
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={onRemove}
            data-testid={`vd-photo-delete-${img.id}`}
            title="Delete photo"
            className="p-1.5 rounded-md bg-white/95 hover:bg-rose-50 text-rose-700 shadow-sm"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
